import React, { useCallback, useContext } from 'react'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { TestDto } from 'api/Clients/Types/TestDto'
import { clientTestTypes, getTestSubcategoryByName } from 'api/Clients/Types/TestQuestionDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { handleOpenWindow } from 'utils/openTestWindow'
import { hasPermission } from 'utils/permissionsValidator'
import { TenantFeature } from 'utils/Tenant'

interface W8BeneficialPageProps {
  data: ClientDetailedDto
  forceRefresh(): void
  updateClientStatus(values: UpdateClientStatus): void
}

const W8BeneficialPage: React.FC<W8BeneficialPageProps> = ({ data }) => {
  const apiClient = useApiClient(ClientsApiClient)
  const [auth] = useContext(AuthSessionContext)

  const permissions = auth?.permissions || []

  const taxFormTestsCallback = useCallback(async () => {
    return await apiClient.getClientTaxFormTests(data.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: tests, isLoading } = useFetchOne(taxFormTestsCallback)

  const handleOpenFilledTest = ({ id, test }: TestDto) => {
    handleOpenWindow({
      urlKey: TenantFeature.clientTestFilledWindow,
      pathParams: {
        type: clientTestTypes.w8beneficial,
        clientId: data.id ? data.id : '',
        testId: id ?? '',
      },
      additionalParams: {
        subcategoryId: getTestSubcategoryByName(test.subcategory).toString(),
      },
    })
  }

  const handleEditTestForm = ({ id, test }: TestDto) => {
    handleOpenWindow({
      permissions,
      permission: PermissionsEnum.CanAddClientTaxFormTests,
      urlKey: TenantFeature.clientEditTestFormWindow,
      pathParams: {
        type: clientTestTypes.w8beneficial,
        clientId: data.id ? data.id : '',
        testId: id ?? '',
      },
      additionalParams: {
        subcategoryId: getTestSubcategoryByName(test.subcategory).toString(),
      },
    })
  }

  return (
    <Loading isLoading={isLoading}>
      <Grid data={tests || []} resizable>
        <Column
          field='test.name'
          title='Name'
          className='table-columns__content'
          cell={(gridCellProps: GridCellProps) => (
            <td
              className='table-columns__link'
              onClick={() => handleOpenFilledTest(gridCellProps.dataItem)}
            >
              {gridCellProps.dataItem.test.name}
            </td>
          )}
        />
        <Column title='User' field='user.name' className='table-columns__content' />
        <Column
          field='dateTaken'
          title='Date Submitted'
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__date'>
              {formatLocalDateUTC(gridCellProps.dataItem.dateTaken, true)}
            </td>
          )}
        />
        <Column field='totalScore' title='Score' className='table-columns__number' />
        <Column field='outcome.name' title='Outcome' className='table-columns__content' />
        {hasPermission(permissions, PermissionsEnum.CanAddClientTaxFormTests) && (
          <Column
            width={50}
            title=''
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => handleEditTestForm(gridCellProps.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        )}
      </Grid>
    </Loading>
  )
}

export default W8BeneficialPage
